import { SongPreview } from '~/interfaces';
import { GameEvent } from '~/modules/GameEvents/GameEvents';
import type { RemotePlayer, RemoteScore, RoomData, RoomState } from './messages';

// Events
export const roomStateChanged = new GameEvent<(room: RoomData | null) => void>('mp:roomStateChanged', false, false);
export const playersChanged = new GameEvent<(players: RemotePlayer[]) => void>('mp:playersChanged', false, false);
export const scoresUpdated = new GameEvent<(scores: Record<string, RemoteScore>) => void>(
  'mp:scoresUpdated',
  false,
  false,
);
export const playbackStartSignal = new GameEvent<(startTimestamp: number) => void>(
  'mp:playbackStartSignal',
  false,
  false,
);
export const songSelectedByHost = new GameEvent<(song: SongPreview) => void>('mp:songSelectedByHost', false, false);
export const countdownStarted = new GameEvent<(song: SongPreview, tolerance: number) => void>(
  'mp:countdownStarted',
  false,
  false,
);
export const showResults = new GameEvent<(scores: Record<string, RemoteScore>) => void>('mp:showResults', false, false);
export const roomError = new GameEvent<(message: string) => void>('mp:roomError');
export const kicked = new GameEvent('mp:kicked');
export const reconnectingChanged = new GameEvent<(reconnecting: boolean) => void>(
  'mp:reconnectingChanged',
  false,
  false,
);

export interface ChatMessage {
  playerId: string;
  playerName: string;
  text: string;
  timestamp: number;
}
export const chatMessageReceived = new GameEvent<(message: ChatMessage) => void>('mp:chatMessage', false, false);

// State store
class RoomStateStore {
  private _room: RoomData | null = null;
  private _myPlayerId: string | null = null;
  private _reconnecting = false;

  get room(): RoomData | null {
    return this._room;
  }

  get myPlayerId(): string | null {
    return this._myPlayerId;
  }

  get isInRoom(): boolean {
    return this._room !== null;
  }

  get reconnecting(): boolean {
    return this._reconnecting;
  }

  setReconnecting(value: boolean): void {
    this._reconnecting = value;
    reconnectingChanged.dispatch(value);
  }

  get isHost(): boolean {
    if (!this._room || !this._myPlayerId) return false;
    const me = this._room.players.find((p) => p.id === this._myPlayerId);
    return me?.isHost ?? false;
  }

  get myPlayer(): RemotePlayer | null {
    if (!this._room || !this._myPlayerId) return null;
    return this._room.players.find((p) => p.id === this._myPlayerId) ?? null;
  }

  get state(): RoomState | null {
    return this._room?.state ?? null;
  }

  get players(): RemotePlayer[] {
    return this._room?.players ?? [];
  }

  get currentSong(): SongPreview | null {
    return this._room?.currentSong ?? null;
  }

  get songQueue(): SongPreview[] {
    return this._room?.songQueue ?? [];
  }

  setRoom(room: RoomData | null, myPlayerId?: string): void {
    this._room = room;
    if (myPlayerId) this._myPlayerId = myPlayerId;
    if (!room) this._myPlayerId = null;
    roomStateChanged.dispatch(room);
    if (room) {
      playersChanged.dispatch(room.players);
      if (room.scores && Object.keys(room.scores).length > 0) {
        scoresUpdated.dispatch(room.scores);
      }
    }
  }

  updatePlayers(players: RemotePlayer[]): void {
    if (this._room) {
      this._room.players = players;
      playersChanged.dispatch(players);
      roomStateChanged.dispatch(this._room);
    }
  }

  updateState(state: RoomState): void {
    if (this._room) {
      this._room.state = state;
      roomStateChanged.dispatch(this._room);
    }
  }

  updateSong(song: SongPreview | null): void {
    if (this._room) {
      this._room.currentSong = song;
      roomStateChanged.dispatch(this._room);
    }
  }

  setSongAndState(song: SongPreview | null, state: RoomState, songQueue?: SongPreview[]): void {
    if (this._room) {
      this._room.currentSong = song;
      this._room.state = state;
      if (songQueue !== undefined) this._room.songQueue = songQueue;
      roomStateChanged.dispatch(this._room);
    }
  }

  updateQueue(queue: SongPreview[]): void {
    if (this._room) {
      this._room.songQueue = queue;
      roomStateChanged.dispatch(this._room);
    }
  }

  updateTolerance(tolerance: number): void {
    if (this._room) {
      this._room.tolerance = tolerance;
    }
  }

  updateScores(scores: Record<string, RemoteScore>): void {
    if (this._room) {
      this._room.scores = scores;
      scoresUpdated.dispatch(scores);
    }
  }

  clear(): void {
    this._room = null;
    this._myPlayerId = null;
    roomStateChanged.dispatch(null);
  }
}

export default new RoomStateStore();
